/**
 * Доменная логика подарочных наборов для миниаппа.
 *
 * Чистые функции над ProductSet — расчёт экономии комплекта, количества
 * чаепитий, сценария и форматирования. Не зависит от React. Переиспользуется
 * в каталоге, карточке товара и корзине.
 */

import type { ProductSet, ProductSetComponent } from '../model/types'

// =============================================================================
// Цена и экономия
// =============================================================================

/** Эффективная цена с учётом скидки. */
export function getSetEffectivePrice(set: ProductSet): number {
  return set.isOnSale && set.salePriceAmount ? set.salePriceAmount : set.effectivePrice || set.priceAmount
}

/** Сумма розничных цен компонентов (как если бы покупали по отдельности). */
export function getSetComponentsAmount(set: ProductSet): number {
  return set.componentsAmount ?? set.components.reduce((sum, c) => sum + c.subtotal, 0)
}

/** Экономия комплекта: сколько покупатель экономит vs покупка по отдельности. */
export function getSetSavings(set: ProductSet): number {
  const savings = getSetComponentsAmount(set) - getSetEffectivePrice(set)
  return savings > 0 ? savings : 0
}

/** Процент экономии (округлённый). */
export function getSetSavingsPercent(set: ProductSet): number {
  const amount = getSetComponentsAmount(set)
  if (amount <= 0) return 0
  const pct = Math.round((getSetSavings(set) / amount) * 100)
  return pct > 0 ? pct : 0
}

// =============================================================================
// Состав
// =============================================================================

export function getSetTeaCount(set: ProductSet): number {
  return set.components.filter((c) => c.productType === 'tea').length
}

export function getSetTeawareCount(set: ProductSet): number {
  return set.components.filter((c) => c.productType === 'teaware').length
}

export function getSetTotalTeaWeight(set: ProductSet): number {
  return set.components.reduce((sum, c) => (c.productType === 'tea' ? sum + (c.weight ?? 0) : sum), 0)
}

/**
 * Оценка количества чаепитий. Эвристика: ~6 г на сессию (гайвань 100 мл,
 * 5–8 проливов). Согласована с website.
 */
export function getSetEstimatedSessions(set: ProductSet): number {
  const grams = getSetTotalTeaWeight(set)
  if (grams <= 0) return 0
  return Math.max(1, Math.round(grams / 6))
}

// =============================================================================
// Сценарий (computable из состава)
// =============================================================================

export type SetScenario = 'intro' | 'ceremony' | 'puer' | 'gaba' | 'oolong' | 'soft' | 'premium' | 'gift'

export interface SetScenarioMeta {
  id: SetScenario
  label: string
}

export function resolveSetScenario(set: ProductSet): SetScenario {
  const hasTeaware = getSetTeawareCount(set) > 0
  const teaNames = set.components.filter((c) => c.productType === 'tea').map((c) => c.productName.toLowerCase())
  const price = getSetEffectivePrice(set)

  const looksPremium = price >= 2500 && !hasTeaware
  const isWuyiOrDancong = teaNames.some(
    (n) => n.includes('дань цун') || n.includes('да хун пао') || n.includes('уишань') || n.includes('жоу гуй'),
  )
  if (looksPremium || (price >= 2500 && isWuyiOrDancong)) return 'premium'
  if (hasTeaware) return 'ceremony'

  const hasPuer = teaNames.some((n) => n.includes('пуэр'))
  const hasGaba = teaNames.some((n) => n.includes('габа'))
  const hasOolong = teaNames.some(
    (n) => n.includes('улун') || n.includes('те гуань') || n.includes('дань цун') || n.includes('да хун пао'),
  )

  if (hasPuer && getSetTeaCount(set) <= 4) return 'puer'
  if (hasGaba && getSetTeaCount(set) <= 4) return 'gaba'
  if (hasOolong) return 'oolong'

  const hasWhite = teaNames.some((n) => n.includes('белый') || n.includes('шоу мэй') || n.includes('бай му дань'))
  const hasRed = teaNames.some((n) => n.includes('красный') || n.includes('дянь хун') || n.includes('золотые брови'))
  if ((hasWhite || hasRed) && getSetTeaCount(set) >= 4) return 'soft'
  if (getSetTeaCount(set) >= 4) return 'intro'

  return 'gift'
}

const SCENARIO_LABELS: Record<SetScenario, string> = {
  intro: 'Первое знакомство',
  ceremony: 'С посудой',
  puer: 'Пуэр',
  gaba: 'ГАБА',
  oolong: 'Улуны',
  soft: 'Мягкий вход',
  premium: 'Премиум',
  gift: 'Подарок',
}

export function getSetScenarioLabel(set: ProductSet): string {
  return SCENARIO_LABELS[resolveSetScenario(set)]
}

// =============================================================================
// Форматирование
// =============================================================================

export function formatSetPrice(value: number): string {
  return `${Math.round(value).toLocaleString('ru-RU')} ₽`
}

function pluralRu(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}

/** «5 чаёв + 2 предмета посуды» / «4 чая» / «3 предмета посуды». */
export function setComponentSummary(set: ProductSet): string {
  const tea = getSetTeaCount(set)
  const teaware = getSetTeawareCount(set)
  if (tea > 0 && teaware > 0) {
    return `${tea} ${pluralRu(tea, 'чай', 'чая', 'чаёв')} + ${teaware} ${pluralRu(teaware, 'предмет', 'предмета', 'предметов')} посуды`
  }
  if (tea > 0) return `${tea} ${pluralRu(tea, 'чай', 'чая', 'чаёв')}`
  if (teaware > 0) return `${teaware} ${pluralRu(teaware, 'предмет', 'предмета', 'предметов')} посуды`
  return 'готовый комплект'
}

/** «Те Гуань Инь · 10 г» / «Гайвань горы · арт. 271». */
export function setComponentLabel(c: ProductSetComponent): string {
  if (c.productType === 'tea' && c.weight) return `${c.productName} · ${c.weight} г`
  if (c.productType === 'teaware' && c.productSku) return `${c.productName} · арт. ${c.productSku}`
  return c.productName
}

/** Главное изображение набора с fallback. */
export function resolveSetImage(set: ProductSet, fallback: string): string {
  return (
    set.primaryImage ||
    set.images?.find((i) => i.isPrimary)?.url ||
    set.images?.[0]?.url ||
    set.components.find((c) => c.productImage)?.productImage ||
    fallback
  )
}
